import type { FreshnessState } from '@/types/status'
import { DEFAULT_REFRESH_INTERVAL, FRESHNESS } from '@/lib/constants'

/**
 * Compute freshness state for a given updatedAt ISO string.
 * @param updatedAt  ISO 8601 UTC string
 * @param refreshInterval  polling interval in ms (default 60_000)
 */
export function computeFreshness(
  updatedAt: string | null | undefined,
  refreshInterval: number = DEFAULT_REFRESH_INTERVAL
): FreshnessState {
  if (!updatedAt) return 'critical'

  const ageMs = Date.now() - new Date(updatedAt).getTime()

  if (ageMs <= FRESHNESS.STALE_THRESHOLD_MULTIPLIER * refreshInterval) {
    return 'fresh'
  }
  if (ageMs <= FRESHNESS.CRITICAL_THRESHOLD_MS) {
    return 'stale'
  }
  return 'critical'
}

/**
 * Compute age in milliseconds from an ISO string.
 */
export function ageMs(updatedAt: string): number {
  // Normalize git-style timestamps ("2026-04-18 14:51:32 -0400") to ISO 8601
  let normalized = updatedAt.replace(/^(\d{4}-\d{2}-\d{2}) (\d{2}:\d{2}:\d{2})/, '$1T$2')
  // Date-only strings: treat as start of day UTC
  if (/^\d{4}-\d{2}-\d{2}$/.test(normalized.trim())) normalized += 'T00:00:00Z'
  const ms = Date.now() - new Date(normalized).getTime()
  return isNaN(ms) ? 0 : ms
}

/**
 * Format a relative time string: "2m ago", "5h ago", "3d ago"
 */
export function relativeTime(updatedAt: string): string {
  const ms = ageMs(updatedAt)
  const secs = Math.floor(ms / 1000)
  if (secs < 60) return `${secs}s ago`
  const mins = Math.floor(secs / 60)
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.floor(hrs / 24)
  return `${days}d ago`
}

/**
 * Format an ISO string to a short human readable date/time.
 * e.g. "Apr 15, 23:30"
 */
export function formatTimestamp(iso: string): string {
  // Normalize Postgres-style timestamps ("2026-04-18 15:00:32.417726-04") to ISO 8601
  const normalized = iso.replace(/^(\d{4}-\d{2}-\d{2}) (\d{2}:\d{2}:\d{2})/, '$1T$2')
  const d = new Date(normalized)
  if (isNaN(d.getTime())) return iso // fallback to raw string
  // Date-only strings ("2026-04-18") — show just the date without time
  if (/^\d{4}-\d{2}-\d{2}$/.test(iso.trim())) {
    return d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })
  }
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })
}

/**
 * Check if an ISO date string is valid.
 */
export function isValidIso(value: string): boolean {
  const d = new Date(value)
  return !isNaN(d.getTime())
}
